import { useLang } from "@/i18n/LanguageContext";

const NAVY = "#0A1128";

export function BrowserFrame({ url = "temha.app/panel", children, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/[0.06] overflow-hidden"
      data-testid={testid}>
      <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
        <div className="flex gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
        </div>
        <span className="flex-1 truncate rounded-md bg-white border border-slate-200 px-3 py-1 text-[10px] text-slate-400 text-center">
          {url}
        </span>
      </div>
      {children}
    </div>
  );
}

function MockBox({ title, rows, badgeCls, testid }) {
  const { t } = useLang();
  return (
    <div className="border border-slate-200 bg-white rounded-md p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold" style={{ color: NAVY }}>{t(title)}</p>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${badgeCls}`}>{rows.length}</span>
      </div>
      <div className="mt-3 space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 border border-slate-100 rounded px-2.5 py-1.5">
            <span className="text-[10px] tabular-nums font-medium w-14 shrink-0 text-slate-500">{r.date}</span>
            <span className="flex-1 min-w-0 truncate text-[11px] text-slate-700">{r.title}</span>
            <span className="text-[11px] font-semibold tabular-nums" style={{ color: NAVY }}>{r.amount}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardMock() {
  const { t } = useLang();
  const payments = [
    { date: "12.08", title: "Örnek Tedarikçi Ltd. — Çek", amount: "48.500,00" },
    { date: "15.08", title: "Demo Lojistik AŞ — Havale", amount: "12.750,00" },
    { date: "20.08", title: "Abonelik — Muhasebe", amount: "3.200,00" },
  ];
  const collections = [
    { date: "11.08", title: "Örnek Market — POS", amount: "27.340,00" },
    { date: "14.08", title: "Örnek Gıda Ltd. — Alınan Çek", amount: "65.000,00" },
    { date: "18.08", title: "DBS — Örnek Bayi", amount: "19.800,00" },
  ];
  const rates = [
    { c: "USD", buy: "47,5229", sell: "47,6085" },
    { c: "EUR", buy: "54,7749", sell: "54,8736" },
  ];
  return (
    <div className="bg-slate-50 p-4 sm:p-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-base font-bold" style={{ color: NAVY }}>Örnek Firma AŞ</p>
          <p className="text-[10px] text-slate-400">{t("Tanıtım verisi — gerçek firma verisi değildir")}</p>
        </div>
        <div className="flex gap-1.5">
          <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-green-100 text-green-800">{t("Aktif")}</span>
          <span className="text-[10px] font-medium px-2 py-0.5 rounded border border-blue-300 text-blue-700">{t("Deneme")}</span>
        </div>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <MockBox title="Yaklaşan Ödemeler" rows={payments} badgeCls="bg-red-50 text-red-700" testid="mock-upcoming-payments" />
        <MockBox title="Yaklaşan Tahsilatlar" rows={collections} badgeCls="bg-green-50 text-green-700" testid="mock-upcoming-collections" />
      </div>
      <div className="mt-3 border border-slate-200 bg-white rounded-md p-4" data-testid="mock-fx-card">
        <p className="text-[13px] font-semibold" style={{ color: NAVY }}>{t("TCMB Efektif Satış")}</p>
        <div className="mt-2 space-y-1.5">
          {rates.map((r) => (
            <div key={r.c} className="flex items-center justify-between border border-slate-100 rounded px-2.5 py-1.5">
              <span className="text-[11px] font-semibold" style={{ color: NAVY }}>{r.c}</span>
              <span className="text-[11px] tabular-nums text-slate-600">{r.buy}</span>
              <span className="text-[11px] tabular-nums font-semibold text-green-700">{r.sell}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MockTable({ title, subtitle, columns, rows, testid }) {
  const { t } = useLang();
  return (
    <div className="bg-white p-4 sm:p-5" data-testid={testid}>
      <p className="text-sm font-bold" style={{ color: NAVY }}>{t(title)}</p>
      {subtitle && <p className="text-[10px] text-slate-400 mt-0.5">{t(subtitle)}</p>}
      <div className="mt-3 overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-left">
          <thead>
            <tr style={{ background: NAVY }}>
              {columns.map((c) => (
                <th key={c} className="px-3 py-2 text-[10px] font-semibold text-white whitespace-nowrap">{t(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                {r.map((cell, j) => (
                  <td key={j} className="px-3 py-2 text-[11px] text-slate-600 whitespace-nowrap">
                    {j === r.length - 1 ? <span className="font-semibold tabular-nums" style={{ color: NAVY }}>{cell}</span> : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PaymentsCollectionsMock() {
  return (
    <div className="bg-slate-50 p-4 sm:p-5 grid sm:grid-cols-2 gap-3">
      <MockTable
        title="Diğer Ödemeler"
        columns={["Tarih", "Firma", "Tutar"]}
        rows={[
          ["10.08.2026", "Örnek Kira — Ofis", "32.500,00 TL"],
          ["14.08.2026", "Demo Muhasebe Ltd.", "7.400,00 TL"],
          ["22.08.2026", "Örnek Enerji AŞ", "4.950,00 TL"],
        ]}
        testid="mock-payments-table"
      />
      <MockTable
        title="Diğer Tahsilatlar"
        columns={["Tarih", "Firma", "Tutar"]}
        rows={[
          ["09.08.2026", "Örnek Gıda Ltd.", "18.250,00 TL"],
          ["16.08.2026", "Demo Perakende AŞ", "9.870,00 TL"],
          ["25.08.2026", "Örnek Bayi — Komisyon", "5.600,00 TL"],
        ]}
        testid="mock-collections-table"
      />
    </div>
  );
}

export function ChecksMock() {
  const { t } = useLang();
  return (
    <div className="bg-slate-50 p-4 sm:p-5">
      <MockTable
        title="Alınan Çekler"
        subtitle="Tanıtım verisi — gerçek firma verisi değildir"
        columns={["Çek No", "Banka", "Vade", "Durum", "Tutar"]}
        rows={[
          ["004512", "Örnek Bank", "20.08.2026", t("Portföyde"), "65.000,00 TL"],
          ["004518", "Demo Bankası", "03.09.2026", t("Portföyde"), "41.750,00 TL"],
          ["004533", "Örnek Bank", "17.09.2026", t("Bankaya Verildi"), "28.900,00 TL"],
        ]}
        testid="mock-checks-table"
      />
    </div>
  );
}

export function CashPosCardMock() {
  const { t } = useLang();
  const cards = [
    { title: "Kasa", lines: [[t("Giriş"), "124.300,00 TL", "text-green-700"], [t("Çıkış"), "58.120,00 TL", "text-red-600"], [t("Bakiye (₺)"), "66.180,00 TL", "font-bold"]] },
    { title: "POS Hareketleri", lines: [[t("Brüt"), "27.340,00 TL", ""], [t("Komisyon"), "492,00 TL", ""], [t("Net"), "26.848,00 TL", "font-bold"]] },
    { title: "Kredi Kartları", lines: [["Örnek Bank • 4582", "Ekstre 12.400,00 TL", ""], [t("Son Ödeme"), "25.08.2026", ""]] },
  ];
  return (
    <div className="bg-slate-50 p-4 sm:p-5 grid sm:grid-cols-3 gap-3">
      {cards.map((c) => (
        <div key={c.title} className="border border-slate-200 bg-white rounded-md p-4" data-testid={`mock-mini-${c.title}`}>
          <p className="text-[13px] font-semibold" style={{ color: NAVY }}>{t(c.title)}</p>
          <div className="mt-2.5 space-y-1.5">
            {c.lines.map(([k, v, cls], i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-[10px] text-slate-400">{k}</span>
                <span className={`text-[11px] tabular-nums ${cls}`} style={cls.includes("font-bold") ? { color: NAVY } : {}}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
